import { useMemo } from 'react'
import clsx from 'clsx'
import { getScoreLabel } from '../utils/scoring'

interface ScoreCircleProps {
  score: number
  size?: 'sm' | 'md' | 'lg'
}

export function ScoreCircle({ score, size = 'lg' }: ScoreCircleProps) {
  const { label, color, description } = getScoreLabel(score)

  const dimensions = useMemo(() => {
    switch (size) {
      case 'sm':
        return { outer: 80, inner: 64, text: 'text-xl', label: 'text-[10px]', stroke: 6 }
      case 'md':
        return { outer: 120, inner: 96, text: 'text-3xl', label: 'text-xs', stroke: 8 }
      case 'lg':
      default:
        return { outer: 160, inner: 128, text: 'text-4xl', label: 'text-sm', stroke: 10 }
    }
  }, [size])

  const strokeWidth = dimensions.stroke
  const radius = (dimensions.outer - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  const { strokeColor, glowColor, bgGlow } = useMemo(() => {
    switch (color) {
      case 'emerald':
        return {
          strokeColor: 'hsl(var(--forge-success))',
          glowColor: 'hsl(var(--forge-success) / 0.4)',
          bgGlow: 'hsl(var(--forge-success) / 0.15)',
        }
      case 'yellow':
        return {
          strokeColor: 'hsl(var(--forge-warning))',
          glowColor: 'hsl(var(--forge-warning) / 0.4)',
          bgGlow: 'hsl(var(--forge-warning) / 0.15)',
        }
      case 'orange':
        return {
          strokeColor: 'hsl(var(--forge-copper))',
          glowColor: 'hsl(var(--forge-copper) / 0.4)',
          bgGlow: 'hsl(var(--forge-copper) / 0.15)',
        }
      case 'red':
        return {
          strokeColor: 'hsl(var(--forge-danger))',
          glowColor: 'hsl(var(--forge-danger) / 0.4)',
          bgGlow: 'hsl(var(--forge-danger) / 0.15)',
        }
      default:
        return {
          strokeColor: 'hsl(var(--forge-success))',
          glowColor: 'hsl(var(--forge-success) / 0.4)',
          bgGlow: 'hsl(var(--forge-success) / 0.15)',
        }
    }
  }, [color])

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Score ring container */}
      <div
        className="score-circle-container"
        style={{ width: dimensions.outer, height: dimensions.outer }}
      >
        {/* Glow effect */}
        <div
          className="score-circle-glow"
          style={{ backgroundColor: bgGlow }}
        />

        {/* SVG ring */}
        <svg
          className="absolute inset-0 -rotate-90"
          width={dimensions.outer}
          height={dimensions.outer}
        >
          {/* Glow filter */}
          <defs>
            <filter id={`glow-${size}`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          {/* Background track */}
          <circle
            cx={dimensions.outer / 2}
            cy={dimensions.outer / 2}
            r={radius}
            fill="none"
            stroke="hsl(var(--forge-bg-surface))"
            strokeWidth={strokeWidth}
          />

          {/* Score arc with glow */}
          <circle
            cx={dimensions.outer / 2}
            cy={dimensions.outer / 2}
            r={radius}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000"
            filter={`url(#glow-${size})`}
            style={{
              filter: `drop-shadow(0 0 8px ${glowColor})`,
            }}
          />

          {/* Tick marks for VU meter effect */}
          {size === 'lg' && Array.from({ length: 20 }).map((_, i) => {
            const angle = (i / 20) * 360 - 90
            const isHighlight = i % 5 === 0
            const x1 = dimensions.outer / 2 + (radius - 6) * Math.cos((angle * Math.PI) / 180)
            const y1 = dimensions.outer / 2 + (radius - 6) * Math.sin((angle * Math.PI) / 180)
            const x2 = dimensions.outer / 2 + (radius - (isHighlight ? 12 : 8)) * Math.cos((angle * Math.PI) / 180)
            const y2 = dimensions.outer / 2 + (radius - (isHighlight ? 12 : 8)) * Math.sin((angle * Math.PI) / 180)

            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="hsl(var(--forge-border-bright))"
                strokeWidth={isHighlight ? 1.5 : 0.75}
                opacity={0.5}
              />
            )
          })}
        </svg>

        {/* Inner content */}
        <div
          className="absolute flex flex-col items-center justify-center bg-forge-elevated rounded-full border border-forge"
          style={{
            width: dimensions.inner,
            height: dimensions.inner,
            left: (dimensions.outer - dimensions.inner) / 2,
            top: (dimensions.outer - dimensions.inner) / 2,
          }}
        >
          <span className={clsx('font-bold font-mono text-white tracking-tight', dimensions.text)}>
            {score}
          </span>
          <span className="text-forge-subtle text-xs font-mono">/100</span>
        </div>
      </div>

      {/* Label */}
      <div className="text-center">
        <span
          className={clsx(
            'font-semibold uppercase tracking-wider',
            dimensions.label,
            color === 'emerald' && 'text-forge-success',
            color === 'yellow' && 'text-forge-warning',
            color === 'orange' && 'text-forge-copper',
            color === 'red' && 'text-forge-danger'
          )}
        >
          {label}
        </span>
        {size === 'lg' && (
          <p className="mt-1.5 text-xs text-forge-muted max-w-[200px]">{description}</p>
        )}
      </div>
    </div>
  )
}
